import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router'
import { supabase } from '../lib/supabase'
import { isKnownCallbackType, stripAuthParamsFromUrl } from './callback-params'
import { initialCallback } from './initial-callback'
import { requestSignInCode } from './otp-request'
import { callSessionGate } from './session-gate'
import { currentPath, safeReturnPath } from './return-url'
import {
  CODE_LENGTH,
  CODE_TTL_MS,
  RESEND_COOLDOWN_MS,
  clearPendingCode,
  readPendingCode,
  writePendingCode,
  type PendingCode,
} from './pending-code'
import type { CodeError, EmailCode, ProviderId } from '../screens/SignInScreen'

/**
 * Client-side floor on how long a sign-in code request appears to take.
 *
 * `otp-request` already holds its own responses to a 400ms floor so that
 * refusals do not return noticeably faster than sends. That work is undone if
 * the UI resolves instantly on a CORS refusal or an offline failure, because
 * "the message appeared immediately" is itself a signal. Cheap to hold, so
 * hold it.
 */
const MIN_CODE_REQUEST_UI_MS = 600

export type AuthState =
  /** Client still resolving whether a session exists (incl. reading a callback). */
  | { status: 'initializing' }
  /** No session. The sign-in screen renders in place of whatever was requested. */
  | { status: 'signed_out' }
  /** A session exists; session-gate has NOT answered yet. Render nothing gated. */
  | { status: 'checking' }
  /** session-gate returned 200. Only now may an authenticated surface render. */
  | {
      status: 'authorized'
      repIds: string[]
      accountType: string | string[]
      email: string | null
    }
  /** session-gate returned 403. */
  | { status: 'not_authorized'; message: string }
  /** The gate could not be reached or failed. Not the same as "refused". */
  | { status: 'error'; detail: string }

interface AuthContextValue {
  state: AuthState
  emailCode: EmailCode
  providerError: string | undefined
  signInWithProvider: (id: ProviderId) => void
  signOut: () => void
  retry: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Supabase answers a wrong, expired, superseded or unknown-address code with
 * the same 403 `otp_expired` "Token has expired or is invalid", so the
 * response cannot say which. "Expired" is therefore worded from when THIS tab
 * asked for the code, which depends only on the clock, never on the address.
 */
function classifyCodeError(
  error: { status?: number | undefined; code?: string | undefined; name?: string },
  requestedAt: number,
): CodeError {
  if (error.status === 429 || error.code === 'over_request_rate_limit') return 'rate_limited'
  if (error.name === 'AuthRetryableFetchError' || !error.status || error.status >= 500) {
    return 'network'
  }
  if (Date.now() - requestedAt > CODE_TTL_MS) return 'expired'
  return 'invalid'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()

  const [state, setState] = useState<AuthState>({ status: 'initializing' })
  // Seeded from sessionStorage so a tab iOS discarded while the rep was in
  // Mail comes back on the code step, not the email step.
  const [pendingCode, setPendingCode] = useState<PendingCode | null>(() => readPendingCode())
  const [codeSending, setCodeSending] = useState(false)
  const [codeVerifying, setCodeVerifying] = useState(false)
  const [codeError, setCodeError] = useState<CodeError | undefined>(undefined)
  const [providerError, setProviderError] = useState<string | undefined>(() => {
    if (!initialCallback.error) return undefined
    // A provider-side or Supabase-side callback error. Unlike an email-code
    // request outcome this one is safe to show: it says nothing about whether any
    // address is on the roster.
    return initialCallback.errorDescription ?? initialCallback.error
  })

  /** Access token whose gate verdict we already have (or are fetching). Stops
   *  the gate being re-called on every token refresh and on StrictMode's
   *  double effect invocation. */
  const gatedToken = useRef<string | null>(null)
  /** True until we have navigated away from the callback URL once. */
  const callbackPending = useRef(initialCallback.isCallback)
  /** True from submitting a code until the gate answers for the session it
   *  produced. The code step sits on the destination route already, so the
   *  only navigation owed is normalising an off-list path to `/`. */
  const codeSignInPending = useRef(false)

  const runGate = useCallback(
    async (accessToken: string, email: string | null) => {
      gatedToken.current = accessToken
      setState({ status: 'checking' })

      const verdict = await callSessionGate(accessToken)

      if (verdict.kind === 'authorized') {
        setState({
          status: 'authorized',
          repIds: verdict.repIds,
          accountType: verdict.accountType,
          email,
        })

        // Land on the intended destination, and only now — after the gate has
        // said yes. Doing this before the verdict would flash a real page at
        // someone about to be refused.
        //
        // Both sign-in paths now redirect DIRECTLY to `SITE_URL + next` — the
        // browser already landed on the right route by the time this runs.
        // `next` as a QUERY PARAM (the old `/auth/callback?next=...` shape)
        // is handled too, as a fallback: if present, navigate there; if
        // absent, there is nothing to navigate to, because the current URL
        // already IS the destination.
        if (callbackPending.current) {
          callbackPending.current = false
          stripAuthParamsFromUrl()
          const target = safeReturnPath(initialCallback.next)
          if (target) navigate(target, { replace: true })
        }
        if (codeSignInPending.current) {
          codeSignInPending.current = false
          navigate(safeReturnPath(currentPath()) ?? '/', { replace: true })
        }
        return
      }

      codeSignInPending.current = false

      if (verdict.kind === 'not_authorized') {
        setState({ status: 'not_authorized', message: verdict.message })
        if (callbackPending.current) {
          callbackPending.current = false
          stripAuthParamsFromUrl()
        }
        // session-gate has already signed this session out server-side. The
        // local tokens are deliberately left alone: a reload simply re-runs the
        // gate and lands here again, which is honest. Forcing a local sign-out
        // here would start overlapping the offboarding-revocation question,
        // which is explicitly out of scope for this task.
        navigate('/not-authorized', { replace: true })
        return
      }

      if (verdict.kind === 'no_session') {
        gatedToken.current = null
        setState({ status: 'signed_out' })
        return
      }

      gatedToken.current = null
      setState({ status: 'error', detail: verdict.detail })
    },
    [navigate],
  )

  useEffect(() => {
    if (initialCallback.isCallback && initialCallback.type) {
      if (!isKnownCallbackType(initialCallback.type)) {
        // Informational only. An unexpected `type` never refuses a session —
        // see callback-params.ts for why narrowing this is the bug to avoid.
        console.warn(
          `auth callback: unexpected type=${initialCallback.type}; proceeding, the session is judged on its tokens`,
        )
      }
    }

    // onAuthStateChange fires INITIAL_SESSION once the client has finished
    // initializing, INCLUDING consuming a callback out of the URL. Waiting for
    // that event rather than calling getSession() immediately is what makes the
    // callback and the cold-load paths the same code.
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        gatedToken.current = null
        clearPendingCode()
        setPendingCode(null)
        setCodeError(undefined)
        setState({ status: 'signed_out' })
        return
      }

      const token = session?.access_token ?? null

      if (!token) {
        gatedToken.current = null
        setState({ status: 'signed_out' })
        return
      }

      // A refresh of a token we have already cleared does not re-run the gate.
      if (token === gatedToken.current) return

      void runGate(token, session?.user.email ?? null)
    })

    return () => data.subscription.unsubscribe()
  }, [runGate])

  const signInWithProvider = useCallback((id: ProviderId) => {
    setProviderError(undefined)

    // OAuth returns in the SAME browser tab it started in — it never had the
    // in-app-webview storage problem magic link has — but it now carries its
    // destination the identical way: `SITE_URL + next`, landing directly on
    // the destination route rather than routing through `/auth/callback`.
    // One mechanism for both sign-in paths, not two.
    const next = safeReturnPath(currentPath())
    const redirectTo = next ? `${window.location.origin}${next}` : window.location.origin

    void supabase.auth
      .signInWithOAuth({
        provider: id,
        options: {
          redirectTo,
          // `email` requested EXPLICITLY rather than left to provider defaults.
          // Without an email claim, sign-in SUCCEEDS and then session-gate has
          // no address to match against rep_mapping — which presents as a
          // broken allowlist rather than as a missing scope. That
          // misattribution is why this is spelled out here.
          scopes: 'email',
        },
      })
      .then(({ error }) => {
        if (error) setProviderError(error.message)
      })
  }, [])

  const requestCode = useCallback((rawEmail: string) => {
    // Normalized exactly as otp-request normalizes it, so the address
    // verifyOtp is called with is the address the code was sent to.
    const email = rawEmail.trim().toLowerCase()
    // otp-request still takes `next` and still builds `emailRedirectTo` from
    // it. The code-only templates never render that URL; the code step sits
    // on the destination route already.
    const next = safeReturnPath(currentPath())

    setCodeSending(true)
    setCodeError(undefined)
    const startedAt = Date.now()

    void (async () => {
      try {
        await requestSignInCode(email, next)
      } catch {
        // Swallowed ON PURPOSE. A network failure, a CORS refusal and a
        // successful send must be indistinguishable in the UI. Surfacing this
        // error would make the screen behave differently for some addresses
        // than others, which is exactly what otp-request's uniform 200 exists
        // to prevent.
      } finally {
        const elapsed = Date.now() - startedAt
        if (elapsed < MIN_CODE_REQUEST_UI_MS) await sleep(MIN_CODE_REQUEST_UI_MS - elapsed)
        // Always the code step. There is no failure branch by design.
        const pending = { email, requestedAt: startedAt }
        writePendingCode(pending)
        setPendingCode(pending)
        setCodeSending(false)
      }
    })()
  }, [])

  const resendCode = useCallback(() => {
    if (!pendingCode) return
    if (Date.now() - pendingCode.requestedAt < RESEND_COOLDOWN_MS) return
    requestCode(pendingCode.email)
  }, [pendingCode, requestCode])

  const verifyCode = useCallback(
    (rawCode: string) => {
      const pending = pendingCode
      const token = rawCode.replace(/\D/g, '')
      if (!pending || token.length !== CODE_LENGTH) return

      setCodeVerifying(true)
      setCodeError(undefined)
      // Set BEFORE the call: verifyOtp emits SIGNED_IN, and so starts the
      // gate, before its own promise resolves.
      codeSignInPending.current = true

      // type 'email' accepts both the signup (confirmation template) and the
      // magic-link token, so first and repeat sign-ins verify the same way.
      // The resulting session reaches runGate through onAuthStateChange like
      // every other session; nothing here touches the gate.
      void supabase.auth.verifyOtp({ email: pending.email, token, type: 'email' }).then(
        ({ error }) => {
          setCodeVerifying(false)
          if (!error) {
            clearPendingCode()
            setPendingCode(null)
            return
          }
          codeSignInPending.current = false
          setCodeError(classifyCodeError(error, pending.requestedAt))
        },
        () => {
          codeSignInPending.current = false
          setCodeVerifying(false)
          setCodeError('network')
        },
      )
    },
    [pendingCode],
  )

  const changeEmail = useCallback(() => {
    clearPendingCode()
    setPendingCode(null)
    setCodeError(undefined)
  }, [])

  const emailCode = useMemo<EmailCode>(
    () => ({
      pending: pendingCode,
      sending: codeSending,
      verifying: codeVerifying,
      error: codeError,
      requestCode,
      verifyCode,
      resendCode,
      changeEmail,
    }),
    [pendingCode, codeSending, codeVerifying, codeError, requestCode, verifyCode, resendCode, changeEmail],
  )

  const signOut = useCallback(() => {
    // A rep signing themselves out. This is NOT the offboarding-revocation gap
    // (session-gate's admin.signOut not firing on deactivation) — that is a
    // separate, measured issue with its own session, and nothing here addresses
    // or affects it.
    void supabase.auth.signOut()
  }, [])

  const retry = useCallback(() => {
    gatedToken.current = null
    setState({ status: 'initializing' })
    void supabase.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token
      if (!token) {
        setState({ status: 'signed_out' })
        return
      }
      void runGate(token, data.session?.user.email ?? null)
    })
  }, [runGate])

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      emailCode,
      providerError,
      signInWithProvider,
      signOut,
      retry,
    }),
    [state, emailCode, providerError, signInWithProvider, signOut, retry],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
